<template>
  <div>
    <!-- Corpo principale del modal -->
    <div class="modal-body" style="min-height: 500px;">
      <div class="image-editor d-flex">
        <!-- Pannello sinistro dei controlli -->
        <div class="d-flex flex-column gap-3 me-4" style="width: 220px;">
          <!-- Zoom -->
          <div class="d-flex flex-column align-items-start">
            <label class="form-label small mb-1">Zoom</label>
            <div class="d-flex align-items-center w-100 gap-2">
              <input type="range" v-model.number="scale" min="0.1" max="3" step="0.1" class="form-range flex-grow-1" />
              <input type="number" class="form-control form-control-sm" :value="Math.round(scale * 100)"
                @input="scale = $event.target.value / 100" style="width: 60px;" />%
            </div>
          </div>

          <!-- Rotazione -->
          <div class="d-flex flex-column align-items-start">
            <label class="form-label small mb-1">Rotazione</label>
            <div class="d-flex align-items-center w-100 gap-2">
              <input type="range" v-model.number="rotation" min="0" max="360" step="1" class="form-range flex-grow-1" />
              <input type="number" v-model.number="rotation" class="form-control form-control-sm"
                style="width: 60px;" />°
            </div>
          </div>

          <!-- Contrasto -->
          <div class="d-flex flex-column align-items-start">
            <label class="form-label small mb-1">Contrasto</label>
            <div class="d-flex align-items-center w-100 gap-2">
              <input type="range" v-model.number="contrast" min="0" max="200" class="form-range flex-grow-1" />
              <input type="number" v-model.number="contrast" class="form-control form-control-sm"
                style="width: 60px;" />%
            </div>
          </div>

          <!-- Luminosità -->
          <div class="d-flex flex-column align-items-start">
            <label class="form-label small mb-1">Luminosità</label>
            <div class="d-flex align-items-center w-100 gap-2">
              <input type="range" v-model.number="brightness" min="0" max="200" class="form-range flex-grow-1" />
              <input type="number" v-model.number="brightness" class="form-control form-control-sm"
                style="width: 60px;" />%
            </div>
          </div>

          <div class="d-flex flex-column align-items-start">
            <label class="form-label small mb-1">Forma ritaglio</label>
            <select v-model="cropShape" class="form-select form-select-sm">
              <option value="rectangle">Rettangolo</option>
              <option value="circle">Cerchio</option>
            </select>
          </div>

          <!-- Nuovi controlli numerici -->
          <div v-if="cropShape === 'rectangle'" class="d-flex flex-column align-items-start">
            <label class="form-label small mb-1">Larghezza</label>
            <input type="number" v-model.number="cropBox.width" class="form-control form-control-sm" />
            <label class="form-label small mb-1 mt-2">Altezza</label>
            <input type="number" v-model.number="cropBox.height" class="form-control form-control-sm" />
          </div>
          <div v-if="cropShape === 'circle'" class="d-flex flex-column align-items-start">
            <label class="form-label small mb-1">Raggio</label>
            <input type="number" v-model.number="circleRadius" class="form-control form-control-sm"
              @input="updateCircleDimensions" />
          </div>


        </div>

        <!-- Canvas di destra -->
        <!-- Canvas di destra -->
        <div class="flex-fill d-flex flex-column align-items-center">
          <!-- Il canvas -->
          <canvas ref="canvas" :width="canvasWidth" :height="canvasHeight" class="border rounded w-100"
            style="max-width: 100%; max-height: 60vh; object-fit: contain; cursor: crosshair;"></canvas>

          <!-- Pulsanti in riga sotto il canvas -->
          <div class="d-flex gap-2 mt-3">
            <button class="btn btn-sm btn-success" @click="applyCrop">Ritaglia</button>
            <button class="btn btn-sm btn-outline-secondary" @click="undo">Indietro</button>
            <button class="btn btn-sm btn-outline-secondary" @click="redo">Avanti</button>
          </div>
        </div>

      </div>
    </div>

    <!-- Footer del modal -->
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" @click="$emit('close')">Chiudi</button>
      <button type="button" class="btn btn-primary" @click="saveImage">Salva</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageEditor',
  props: {
    file: { type: Object, required: true }
  },
  data() {
    return {
      canvasWidth: 800,
      canvasHeight: 600,
      ctx: null,
      scale: 1,
      rotation: 0,
      contrast: 100,
      brightness: 100,
      cropBox: { x: 300, y: 200, width: 200, height: 200 },
      cropShape: 'rectangle',
      editMode: 'drag',
      draggingBox: false,
      dragOffset: { x: 0, y: 0 },
      circleRadius: 100,
      image: new Image(),
      undoStack: [],
      redoStack: [],
    };
  },
  watch: {
    scale: 'drawImage',
    rotation: 'drawImage',
    contrast: 'drawImage',
    brightness: 'drawImage',
    cropBox: {
      deep: true,
      handler: 'drawImage'
    },
    cropShape(newVal) {
      if (newVal === 'circle') {
        this.circleRadius = Math.min(this.cropBox.width, this.cropBox.height) / 2;
      }
    }
  },
  mounted() {
    this.ctx = this.$refs.canvas.getContext('2d');
    this.loadImageFromFTP();
    this.$refs.canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
  },
  methods: {
    updateCircleDimensions() {
      this.cropBox.width = this.cropBox.height = this.circleRadius * 2;
      this.drawImage();
    },
    /* ---------------------- Undo / Redo ---------------------- */
    saveToUndoStack() {
      this.undoStack.push(this.captureCurrentState());
      this.redoStack = [];
    },
    captureCurrentState() {
      /*  🔑  Salviamo SOLO il contenuto dell’immagine (this.image.src)
          invece del canvas – così evitiamo di “congelare” i rettangoli
          di selezione nel dato salvato e otteniamo uno stato pulito.   */
      return {
        imageSrc: this.image.src,
        state: {
          scale: this.scale,
          rotation: this.rotation,
          contrast: this.contrast,
          brightness: this.brightness,
          cropBox: { ...this.cropBox },
          cropShape: this.cropShape,
          circleRadius: this.circleRadius,
          canvasWidth: this.canvasWidth,
          canvasHeight: this.canvasHeight
        }
      };
    },
    restoreState(saved) {
      const s = saved.state;

      // ripristina proprietà "primitive"
      this.canvasWidth = s.canvasWidth;
      this.canvasHeight = s.canvasHeight;
      this.scale = s.scale;
      this.rotation = s.rotation;
      this.contrast = s.contrast;
      this.brightness = s.brightness;
      this.cropBox = { ...s.cropBox };
      this.cropShape = s.cropShape;
      this.circleRadius = s.circleRadius;

      // ripristina l’immagine
      const img = new Image();
      img.onload = () => {
        this.image = img;
        this.$refs.canvas.width = this.canvasWidth;
        this.$refs.canvas.height = this.canvasHeight;
        this.ctx = this.$refs.canvas.getContext('2d');
        this.drawImage();
        this.$nextTick(() => this.$forceUpdate());
      };
      img.src = saved.imageSrc;
    },
    undo() {
      if (!this.undoStack.length) return;
      this.redoStack.push(this.captureCurrentState());   // stato corrente → redo
      const previous = this.undoStack.pop();             // stato precedente
      this.restoreState(previous);
    },

    redo() {
      if (!this.redoStack.length) return;
      this.undoStack.push(this.captureCurrentState());   // stato corrente → undo
      const next = this.redoStack.pop();                 // stato successivo
      this.restoreState(next);
    },
    loadFromDataURL(dataURL) {
      const img = new Image();
      img.onload = () => {
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.ctx.drawImage(img, 0, 0);
        this.emitImage();
      };
      img.src = dataURL;
    },

    /* ---------------------- Caricamento immagine ---------------------- */
    getPreviewUrl(file) {
      return `${utils.BASE_API}ftp/get-preview?path=${encodeURIComponent(file.fullPath)}`;
    },
    async loadImageFromFTP() {
      try {
        const res = await fetch(this.getPreviewUrl(this.file));
        const blob = await res.blob();
        const reader = new FileReader();
        reader.onload = () => {
          this.image.onload = this.drawImage;
          this.image.src = reader.result;
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Errore nel caricamento immagine FTP:', err);
      }
    },

    /* ---------------------- Interazione ---------------------- */
    getHandlePositions() {
      const { x, y, width, height } = this.cropBox;
      return {
        nw: { x, y },
        ne: { x: x + width, y },
        sw: { x, y: y + height },
        se: { x: x + width, y: y + height }
      };
    },
    onMouseDown(e) {
      const mouse = this.getMousePos(e);
      const { x, y, width, height } = this.cropBox;

      // Reset dello stato precedente
      this.draggingHandle = null;
      this.draggingBox = false;

      if (this.editMode === 'resize') {
        for (const [key, pos] of Object.entries(this.getHandlePositions())) {
          const handleSize = 12; // Deve corrispondere alla dimensione delle maniglie
          if (
            mouse.x >= pos.x - handleSize &&
            mouse.x <= pos.x + handleSize &&
            mouse.y >= pos.y - handleSize &&
            mouse.y <= pos.y + handleSize
          ) {
            this.draggingHandle = key;
            this.saveToUndoStack();
            e.preventDefault();
            return;
          }
        }
      }

      if (this.editMode === 'drag') {
        if (mouse.x > x && mouse.x < x + width && mouse.y > y && mouse.y < y + height) {
          this.draggingBox = true;
          this.dragOffset = {
            x: mouse.x - x,
            y: mouse.y - y
          };
          this.saveToUndoStack();
          e.preventDefault();
        }
      }
    },
    onMouseMove(e) {
      const mouse = this.getMousePos(e);
      if (this.draggingHandle) {
        const { x: origX, y: origY, width: origW, height: origH } = this.cropBox;
        const minSize = 20; // Aumentato per maggiore usabilità
        let newX = origX, newY = origY, newWidth = origW, newHeight = origH;

        // Calcola i nuovi parametri in base alla maniglia
        switch (this.draggingHandle) {
          case 'nw':
            newWidth = origW + (origX - mouse.x);
            newHeight = origH + (origY - mouse.y);
            newX = Math.min(mouse.x, origX + origW - minSize);
            newY = Math.min(mouse.y, origY + origH - minSize);
            break;
          case 'ne':
            newWidth = mouse.x - origX;
            newHeight = origH + (origY - mouse.y);
            newY = Math.min(mouse.y, origY + origH - minSize);
            break;
          case 'sw':
            newWidth = origW + (origX - mouse.x);
            newHeight = mouse.y - origY;
            newX = Math.min(mouse.x, origX + origW - minSize);
            break;
          case 'se':
            newWidth = mouse.x - origX;
            newHeight = mouse.y - origY;
            break;
        }

        // Applica dimensioni minime
        newWidth = Math.max(newWidth, minSize);
        newHeight = Math.max(newHeight, minSize);

        // Regola le coordinate in base alle nuove dimensioni
        switch (this.draggingHandle) {
          case 'nw':
            newX = origX + origW - newWidth;
            newY = origY + origH - newHeight;
            break;
          case 'ne':
            newY = origY + origH - newHeight;
            break;
          case 'sw':
            newX = origX + origW - newWidth;
            break;
        }

        // Mantieni il crop box dentro i bordi del canvas
        const maxX = this.canvasWidth - newWidth;
        const maxY = this.canvasHeight - newHeight;
        newX = Math.max(0, Math.min(newX, maxX));
        newY = Math.max(0, Math.min(newY, maxY));

        // Aggiorna solo se ci sono cambiamenti
        if (newX !== this.cropBox.x || newY !== this.cropBox.y ||
          newWidth !== this.cropBox.width || newHeight !== this.cropBox.height) {
          this.cropBox = {
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight
          };
          this.drawImage();
        }

        e.preventDefault();
      } else if (this.draggingBox) {
        // Calcola la nuova posizione con limiti del canvas
        const newX = Math.max(0, Math.min(
          mouse.x - this.dragOffset.x,
          this.canvasWidth - this.cropBox.width
        ));

        const newY = Math.max(0, Math.min(
          mouse.y - this.dragOffset.y,
          this.canvasHeight - this.cropBox.height
        ));

        if (newX !== this.cropBox.x || newY !== this.cropBox.y) {
          this.cropBox.x = newX;
          this.cropBox.y = newY;
          this.drawImage();
        }
        e.preventDefault();
      }
    },
    onMouseUp() {
      // Aggiungi un controllo aggiuntivo per il canvas
      if (this.draggingHandle || this.draggingBox) {
        this.$nextTick(() => {
          this.drawImage();
        });
      }
      this.draggingHandle = null;
      this.draggingBox = false;
    },

    /* ---------------------- Disegno ---------------------- */
    drawImage() {
      if (!this.ctx || !this.image.complete) return;

      const { canvasWidth: w, canvasHeight: h } = this;
      this.ctx.clearRect(0, 0, w, h);

      /* --- immagine con trasformazioni --- */
      this.ctx.save();
      this.ctx.translate(w / 2, h / 2);
      this.ctx.rotate((this.rotation * Math.PI) / 180);
      this.ctx.scale(this.scale, this.scale);
      this.ctx.filter = `contrast(${this.contrast}%) brightness(${this.brightness}%)`;
      this.ctx.drawImage(this.image, -this.image.width / 2, -this.image.height / 2);
      this.ctx.restore();

      /* --- rettangolo + maniglie SOLO se la selezione è “vera” --- */
      const fullCanvas =
        this.cropBox.x === 0 &&
        this.cropBox.y === 0 &&
        this.cropBox.width === w &&
        this.cropBox.height === h;

      if (!fullCanvas) {
        const { x, y, width, height } = this.cropBox;
        this.ctx.strokeStyle = 'blue';
        this.ctx.lineWidth = 2;

        if (this.cropShape === 'circle') {
          const r = Math.min(width, height) / 2;
          this.ctx.beginPath();
          this.ctx.arc(x + width / 2, y + height / 2, r, 0, Math.PI * 2);
          this.ctx.stroke();
        } else {
          this.ctx.strokeRect(x, y, width, height);
        }

        // maniglie
        for (const pos of Object.values(this.getHandlePositions())) {
          this.ctx.fillStyle = 'white';
          this.ctx.strokeStyle = 'black';
          this.ctx.fillRect(pos.x - 6, pos.y - 6, 12, 12);
          this.ctx.strokeRect(pos.x - 6, pos.y - 6, 12, 12);
        }
      }

      this.emitImage();
    },

    /* ---------------------- Helpers ---------------------- */
    getMousePos(e) {
      if (!this.$refs.canvas) return { x: 0, y: 0 }; // protezione contro null

      const rect = this.$refs.canvas.getBoundingClientRect();
      const scaleX = this.$refs.canvas.width / rect.width;
      const scaleY = this.$refs.canvas.height / rect.height;

      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      const centerX = this.canvasWidth / 2;
      const centerY = this.canvasHeight / 2;

      let point = { x: x - centerX, y: y - centerY };

      const angle = -this.rotation * Math.PI / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const newX = point.x * cos - point.y * sin;
      const newY = point.x * sin + point.y * cos;

      point.x = newX / this.scale + centerX;
      point.y = newY / this.scale + centerY;

      return point;
    },
    applyCrop() {
      /* 1 — salva lo stato per UNDO */
      this.saveToUndoStack();

      const { x, y, width, height } = this.cropBox;

      /* 2 — canvas temporaneo con l’area ritagliata */
      const tmp = document.createElement('canvas');
      tmp.width = width;
      tmp.height = height;
      const tctx = tmp.getContext('2d');

      tctx.save();
      if (this.cropShape === 'circle') {
        tctx.beginPath();
        tctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
        tctx.clip();
      }
      tctx.translate(width / 2, height / 2);
      tctx.rotate((this.rotation * Math.PI) / 180);
      tctx.scale(this.scale, this.scale);
      tctx.filter = `contrast(${this.contrast}%) brightness(${this.brightness}%)`;

      const dx = this.canvasWidth / 2 - x - width / 2;
      const dy = this.canvasHeight / 2 - y - height / 2;
      tctx.drawImage(this.image, -this.image.width / 2 + dx, -this.image.height / 2 + dy);
      tctx.restore();

      /* 3 — mostriamo SUBITO il risultato (niente “schermo bianco”) */
      this.canvasWidth = width;
      this.canvasHeight = height;
      this.$refs.canvas.width = width;
      this.$refs.canvas.height = height;
      this.ctx = this.$refs.canvas.getContext('2d');
      this.ctx.clearRect(0, 0, width, height);
      this.ctx.drawImage(tmp, 0, 0);

      /* 4 — carichiamo in background lo snapshot
            e *solo allora* resettiamo i controlli:
            così i watcher ridisegnano dopo che l’immagine è pronta. */
      const dataURL = tmp.toDataURL();
      const newImg = new Image();
      newImg.onload = () => {
        this.image = newImg;

        // reset controlli / stato “pulito”
        this.scale = 1;
        this.rotation = 0;
        this.contrast = 100;
        this.brightness = 100;
        this.cropBox = { x: 0, y: 0, width, height };

        this.drawImage();   // ridisegno coerente
        this.emitImage();   // aggiorna eventuale anteprima esterna
      };
      newImg.src = dataURL;
    }

    ,


    emitImage() {
      const isPng = /\.png$/i.test(this.file.name);
      const mimeType = isPng ? 'image/png' : 'image/jpeg';
      const dataURL = this.$refs.canvas.toDataURL(mimeType);
      this.$emit('update:image', dataURL);
    },

    saveImage() {
      const isPng = /\.png$/i.test(this.file.name);
      const mimeType = isPng ? 'image/png' : 'image/jpeg';
      const base64 = this.$refs.canvas.toDataURL(mimeType).split(',')[1];

      utils.ajax('ftp/save-preview-image', {
        path: this.file.fullPath,
        content: base64
      }, (res) => {
        if (res.esito == 0) {
          utils.alert.success('🖼️ Immagine salvata con successo!');
          this.$emit('update:image', base64);
          this.$emit('salvato');
        } else {
          utils.alert.error('❌ Errore durante il salvataggio.');
        }
      });
    }
  }
};
</script>

<style scoped>
.image-editor {
  display: flex;
  align-items: flex-start;
}

canvas {
  image-rendering: -moz-crisp-edges;
  image-rendering: pixelated;
}

.control-panel {
  min-width: 220px;
  max-height: 60vh;
  overflow-y: auto;
}
</style>
